import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp, HoverHintWithGlyph } from "@/components/ui/title-help";
import { formatNumber } from "@/lib/mrp";
import {
  MRP_RFQ_STATUS_HOVER_HINT,
  MRP_RFQ_STATUS_LABEL,
  fetchMrpRfqs,
  mrpRfqStatusBadgeTone,
  type MrpRfqListRow,
  type MrpRfqStatus,
} from "@/lib/rfqApi";

type StatusFilter = "all" | MrpRfqStatus;
type RfqSortKey =
  | "rfq_number"
  | "created_at"
  | "supplier_name"
  | "line_count"
  | "total_target_quantity"
  | "needed_by_date"
  | "assignee"
  | "status";

const RFQ_LIST_STALE_MS = 30_000;
const RFQ_SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [value, delayMs]);
  return debounced;
}

function compareRfqs(
  left: MrpRfqListRow,
  right: MrpRfqListRow,
  key: RfqSortKey,
  dir: SortDirection,
): number {
  const factor = dir === "asc" ? 1 : -1;
  const read = (row: MrpRfqListRow): string | number => {
    switch (key) {
      case "rfq_number":
        return row.rfq_number;
      case "created_at":
        return row.created_at;
      case "supplier_name":
        return row.supplier_name ?? "";
      case "line_count":
        return row.line_count;
      case "total_target_quantity":
        return row.total_target_quantity;
      case "needed_by_date":
        return row.needed_by_date ?? "";
      case "assignee":
        return row.assigned_to_display_name?.trim() ?? "";
      case "status":
        return row.status;
      default:
        return "";
    }
  };
  const leftValue = read(left);
  const rightValue = read(right);
  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return (leftValue - rightValue) * factor;
  }
  return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true }) * factor;
}

const RFQ_COLUMNS: HubDataTableColumn<MrpRfqListRow, RfqSortKey>[] = [
  {
    id: "rfq_number",
    header: "RFQ",
    sortKey: "rfq_number",
    overflow: "nowrap",
    sortValue: (row) => row.rfq_number,
    cell: (row) => (
      <Link to={`/mrp/rfqs/${row.id}`} className="font-mono text-xs text-brand-700 hover:text-brand-900 hover:underline">
        {row.rfq_number}
      </Link>
    ),
  },
  {
    id: "created_at",
    header: "Created",
    sortKey: "created_at",
    overflow: "nowrap",
    sortValue: (row) => row.created_at,
    cell: (row) => <span className="text-xs">{row.created_at.slice(0, 10)}</span>,
  },
  {
    id: "supplier_name",
    header: "Supplier",
    sortKey: "supplier_name",
    sortValue: (row) => row.supplier_name ?? "",
    cell: (row) => <span className="text-xs">{row.supplier_name ?? "—"}</span>,
  },
  {
    id: "line_count",
    header: "Lines",
    sortKey: "line_count",
    align: "right",
    sortValue: (row) => row.line_count,
    cell: (row) => <span className="font-mono text-xs">{row.line_count}</span>,
  },
  {
    id: "total_target_quantity",
    header: "Qty",
    sortKey: "total_target_quantity",
    align: "right",
    sortValue: (row) => row.total_target_quantity,
    cell: (row) => <span className="font-mono text-xs">{formatNumber(row.total_target_quantity)}</span>,
  },
  {
    id: "needed_by_date",
    header: "Needed by",
    sortKey: "needed_by_date",
    sortValue: (row) => row.needed_by_date ?? "",
    cell: (row) => <span className="text-xs">{row.needed_by_date ?? "—"}</span>,
  },
  {
    id: "assignee",
    header: "Assignee",
    sortKey: "assignee",
    sortValue: (row) => row.assigned_to_display_name?.trim() ?? "",
    cell: (row) => (
      <span className="text-xs">
        {row.assigned_to_display_name?.trim() || (
          <span className="text-amber-700">Unassigned</span>
        )}
      </span>
    ),
  },
  {
    id: "status",
    header: (
      <span className="inline-flex items-center gap-0.5">
        Status
        <HoverHintWithGlyph hoverHint={MRP_RFQ_STATUS_HOVER_HINT} />
      </span>
    ),
    sortKey: "status",
    sortValue: (row) => row.status,
    cell: (row) => (
      <Badge tone={mrpRfqStatusBadgeTone(row.status)}>{MRP_RFQ_STATUS_LABEL[row.status]}</Badge>
    ),
  },
];

export function RfqList() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<RfqSortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const debouncedSearch = useDebouncedValue(search.trim(), RFQ_SEARCH_DEBOUNCE_MS);

  const rfqsQuery = useQuery({
    queryKey: ["mrp", "rfqs", statusFilter, debouncedSearch],
    queryFn: () =>
      fetchMrpRfqs({
        status: statusFilter === "all" ? undefined : statusFilter,
        q: debouncedSearch || undefined,
      }),
    staleTime: RFQ_LIST_STALE_MS,
    placeholderData: keepPreviousData,
  });

  const loadedRows = rfqsQuery.data ?? [];
  const rows = useMemo(
    () => [...loadedRows].sort((left, right) => compareRfqs(left, right, sortKey, sortDir)),
    [loadedRows, sortKey, sortDir],
  );
  const isInitialLoad = rfqsQuery.isPending && loadedRows.length === 0;
  const filtersActive = statusFilter !== "all" || debouncedSearch !== "";

  function toggleSort(key: RfqSortKey) {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "created_at" || key === "line_count" || key === "total_target_quantity" ? "desc" : "asc");
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Create request-for-quote documents for one supplier, attach specs, mark sent, record quoted prices, then start a purchase order from the quote.">
          RFQ
        </PageTitleWithHelp>
        <Link
          to="/mrp/rfqs/compose"
          className="inline-flex h-9 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-medium text-white transition-colors hover:bg-brand-700"
        >
          New RFQ
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="rfq-search">Search</Label>
            <Input
              id="rfq-search"
              type="search"
              placeholder="Filter by ID or supplier…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div>
            <Label>Status</Label>
            <div className="flex flex-wrap gap-1 pt-1">
              {(["all", "draft", "sent", "quoted", "converted", "cancelled"] as const).map((value) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={statusFilter === value ? "primary" : "secondary"}
                  onClick={() => setStatusFilter(value)}
                >
                  {value === "all" ? "All" : MRP_RFQ_STATUS_LABEL[value]}
                </Button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          isInitialLoad
            ? "Loading RFQs…"
            : rows.length === 1
              ? "1 RFQ"
              : `${rows.length} RFQs`
        }
        columns={RFQ_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={isInitialLoad}
        loadingMessage="Loading RFQs…"
        error={rfqsQuery.isError ? (rfqsQuery.error as Error).message : null}
        empty="No RFQs yet."
        isFiltered={filtersActive}
        filteredEmpty="No RFQs match these filters."
      />
    </div>
  );
}
